<template>
  <section v-if="displayItems.length" class="PinnedChatHistory">
    <h2 class="PinnedChatHistory__title">Pinned</h2>

    <ul class="PinnedChatHistory__list">
      <li
        v-for="chat in displayItems"
        :key="chat.id"
        class="PinnedChatHistory__row"
        :data-drop-target="isDropTarget(chat.id)"
        :data-drag-source="isDragSource(chat.id)"
        draggable="true"
        aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
        @dragstart="onDragStart($event, chat.id)"
        @dragend="onDragEnd()"
        @dragover="onDragOver($event, chat.id)"
        @drop="onDrop($event)"
        @click.capture="onRowClick"
        @keydown="onRowKeydown($event, chat.id)"
      >
        <ChatHistoryRow :chat="chat" @onChatClick="$emit('onChatClick')" />
      </li>
    </ul>
  </section>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'

import { useChatStore } from '@/store/chat'

import ChatHistoryRow from './ChatHistoryRow.vue'
import { useDragReorderList } from '../composables/useDragReorderList'

defineEmits(['onChatClick'])

const chatStore = useChatStore()
const { pinnedChats } = storeToRefs(chatStore)

const {
  displayItems,
  isDropTarget,
  isDragSource,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onRowClick,
  onRowKeydown,
} = useDragReorderList({
  items: pinnedChats,
  rowSelector: '.PinnedChatHistory__row',
  // The store owns the pinned list — it applies the dropped order to the caches at once
  // and debounces the request — so a reorder, a pin and an unpin all build on one record
  // instead of each sending a list built from the copy it happened to read.
  onCommit: (_chatIds, chats) => chatStore.reorderPinnedChats(chats),
})
</script>

<style scoped lang="scss">
.PinnedChatHistory {
  display: flex;
  flex-direction: column;

  .PinnedChatHistory__title {
    @include font-caption-medium;
    color: var(--text-tertiary);
    font-feature-settings:
      'liga' off,
      'clig' off;
    margin: 0;
    padding: 0 var(---fds-global-space-xs) var(---fds-global-space-xs);
  }

  .PinnedChatHistory__list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin: 0;
    padding: 0;
  }

  .PinnedChatHistory__row {
    list-style: none;
    border-radius: var(--radius-xs);
    cursor: grab;
    transition: background-color 0.15s ease-in-out;

    &:active {
      cursor: grabbing;
    }

    &[data-drop-target='true'] {
      background-color: var(--interactive-bg-tertiary-hover);
    }

    &[data-drag-source='true'] :deep(.TheSidebarMenuItem) {
      opacity: 0.35;
      transition: opacity 0.15s ease-in-out;
    }

    // The section heading already says "Pinned", so a marker on every row inside it only
    // repeats itself — it stays hidden here and the leading slot closes up entirely, so a
    // pinned title lines up with the chats listed below instead of sitting behind an
    // empty gutter. Hovering slides the drag handle back in, which does nudge the title
    // across; that motion is the accepted price of holding no space at rest.
    //
    // Closing the slot takes two declarations, and both belong on the icon wrapper —
    // `TheSidebarMenuItem`'s own flex child. Zeroing its width still leaves that flex
    // row's `gap` between it and the label, which the negative margin cancels; putting
    // either on the inner `.ChatHistoryItem__leading` cannot reach across the wrapper and
    // leaves the title a `gap` short of the unpinned rows. Animating them (rather than
    // toggling `display`) eases the handle in and lets the title move smoothly — the same
    // trick the trailing toggles use in ChatHistoryItem.
    //
    // The marker still earns its slot in the search modal, where pinned chats sit in date
    // groups with no heading to lean on — hence hiding it here in CSS rather than
    // dropping it from the row.
    :deep(.ChatHistoryItem__pin-icon) {
      opacity: 0;
    }

    :deep(.TheSidebarMenuItem__icon) {
      width: 0;
      margin-right: calc(-1 * var(---fds-global-space-xs));
      overflow: hidden;
      transition:
        width 0.2s ease-in-out,
        margin-right 0.2s ease-in-out;
    }

    &:hover,
    &[data-drop-target='true'],
    &[data-drag-source='true'] {
      :deep(.TheSidebarMenuItem__icon) {
        width: 1.25rem;
        margin-right: 0;
      }

      :deep(.ChatHistoryItem__drag-handle) {
        opacity: 1;
      }
    }
  }
}
</style>
